import { useCallback, useMemo, useRef, useState, Suspense } from 'react';
import { lazyWithReload } from './lib/lazyWithReload';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import './App.css';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import ResponsiveNavbar from './components/ResponsiveNavbar/ResponsiveAppBar';
import 'react-toastify/dist/ReactToastify.css';
import { AuthContext, EnterpriseType, UserDetailsContext } from './contexts';
import { S3CredentialsContext } from './contexts';
import { useGetUser } from './hooks/useGetUser';
import PageLayout from './components/PageLayout/PageLayout';
import Mixpanel from './utils/mixpanel';
import Footer from './components/Footer/Footer';
import { getStorageUsage } from './services/storageService';
import { Enterprise, EnterpriseDetails, GetEnterprisesForUserResponse, Role } from './proto/api_pb';
import { SubscriptionTier } from './proto/api_pb';
import { SubscriptionState } from './proto/api_pb';
import useGetValidAccessToken from './hooks/useGetValidAccessToken';
import { useGetAllUsers } from './hooks/useGetAllUsers';
import { useS3Credentials } from './hooks/useS3Credentials';
import { FileUploadStatusCard } from './components/FileUploadStatusCard/FileUploadStatusCard';
import { FolderUploadInfo, UploadCardStatusInfo } from './components/FileUploadStatusCard/types';
import { UploadedFileInfo } from './components/FileUploadStatusCard/types';
import { isEnterprise } from './theme';
import { hotjar } from 'react-hotjar';
import Loading from './pages/Loading';
import { getPaymentMethod } from './components/TrackComponents/TrackHandler/trackHandlers';
import { routesWithoutFooter } from './components/constants';
import { routesWithoutNavbar } from './components/constants';
import {
  handleLastActive,
  checkEnterpriseAddendumAccepted,
  updateEmail,
} from './services/userService';
import { getAllUserEnterprises } from './services/enterpriseService';
import { OpenFoldersListType } from './pages/Tracks/types';
import { DrawerContainer } from './components/TracksPageComponents/styles';
import { Drawer } from './components/TracksPageComponents/Drawer';
import { hiddenDrawerRoutes } from './helper';
import {
  GuestClaimGate,
  GuestTermsGateProvider,
  useIsGuest,
  useGuestSessionHealth,
} from './components/Guest';
import { stateMap, tierMap } from './pages/Billing/types';
import { fetchTracks } from './services/trackService';
import { getStoredGuestSession } from './services/guestSessionService';
import { registerUser } from './services/registerUserService';
import { Profile } from './pages/ProfileSettings/types';
import { GetMyTracksResponse } from './proto/api_pb';
import { TrackFilterParams } from './pages/MyTracks/types';

// Route-level code splitting — each page loads only when navigated to
const NotFound = lazyWithReload(() => import('./pages/NotFound'));
const ShareManagement = lazyWithReload(() => import('./pages/ShareManagement/ShareManagement'));
const TeamManagement = lazyWithReload(() =>
  import('./pages/TeamManagement/TeamManagement').then((m) => ({ default: m.TeamManagement }))
);
const PrivacyPolicy = lazyWithReload(() => import('./pages/Legal/Privacy'));
const CreatorAgreement = lazyWithReload(() => import('./pages/Legal/CreatorAgreement'));
const TermsOfUse = lazyWithReload(() => import('./pages/Legal/TermsOfUse'));
const EnterpriseAddendum = lazyWithReload(() => import('./pages/Legal/EnterpriseAddendum'));
const WelcomePage = lazyWithReload(() => import('./pages/Welcome/WelcomePage'));
const GuestAutoStartPage = lazyWithReload(() => import('./pages/GuestAutoStart/GuestAutoStartPage'));
const Billing = lazyWithReload(() => import('./pages/Billing/Billing'));
const Success = lazyWithReload(() => import('./pages/Billing/Success'));
const PaymentCancel = lazyWithReload(() => import('./pages/Billing/PaymentCancel'));
const PricingTable = lazyWithReload(() => import('./pages/PricingTable/PricingTable'));
const DefaultPage = lazyWithReload(() => import('./pages/DefaultPage'));
const SharedContentPage = lazyWithReload(() =>
  import('./pages/SharedContentPage/SharedContentPage').then((m) => ({
    default: m.SharedContentPage,
  }))
);
const SharedFolderPage = lazyWithReload(() => import('./pages/SharedContentPage/SharedFolderPage'));
const SharedProjectsPage = lazyWithReload(() =>
  import('./pages/SharedContentPage/SharedProjectsPage').then((m) => ({
    default: m.SharedProjectsPage,
  }))
);
const InitialSettings = lazyWithReload(() => import('./pages/InitialSettings/InitialSettings'));
const TracksPage = lazyWithReload(() =>
  import('./pages/Tracks/TracksPage').then((m) => ({ default: m.TracksPage }))
);
const ExplorePage = lazyWithReload(() =>
  import('./pages/ExplorePage/ExplorePage').then((m) => ({ default: m.ExplorePage }))
);
const ProfilePage = lazyWithReload(() =>
  import('./pages/ProfilePage/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const Projects = lazyWithReload(() =>
  import('./pages/Projects/Projects').then((m) => ({ default: m.Projects }))
);
const SelectEnterprise = lazyWithReload(() =>
  import('./pages/Enterprise/SelectEnterprise').then((m) => ({ default: m.SelectEnterprise }))
);
const Dashboard = lazyWithReload(() => import('./pages/DashboardPage/DashboardPage'));
const SupportTicketsPage = lazyWithReload(() => import('./pages/SupportTickets/SupportTicketsPage'));
const AdminStatsPage = lazyWithReload(() => import('./pages/AdminStats/AdminStatsPage'));
const ComponentsPreview = lazyWithReload(() => import('./pages/dev/ComponentsPreview'));
const UserProfile = lazyWithReload(() =>
  import('./components/UserProfile/UserProfile').then((m) => ({ default: m.UserProfile }))
);
// Modals loaded lazily — only fetched when their open condition becomes true
const ConsentModal = lazyWithReload(() => import('./components/Modal/ConsentModal'));
const CompleteNameModal = lazyWithReload(() => import('./components/Modal/CompleteNameModal'));
const CorrectEmailModal = lazyWithReload(() => import('./components/Modal/CorrectEmailModal'));

const App: React.FC = () => {
  const { loginWithRedirect, getIdTokenClaims, isAuthenticated, isLoading, user } = useAuth0();
  // Drop any guest session the current backend can't accept (e.g. dev-signed
  // tokens cached while app.ommuse.com was mis-routed to the dev API) and
  // auto-start a fresh one. Must run before guest RPCs fire.
  useGuestSessionHealth();
  const isGuest = useIsGuest();
  const [userId, setUserId] = useState('');
  const [storageUsage, setStorageUsage] = useState<number | null>(null);
  const [successReg, setSuccessReg] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean>(false);
  const [uploadingFilesDetailsList, setUploadingFilesDetailsList] = useState<UploadedFileInfo[]>(
    []
  );
  const [uploadingCardsStatusList, setUploadingCardsStatusList] = useState<UploadCardStatusInfo[]>(
    []
  );
  const [enterprises, setEnterprises] = useState<EnterpriseDetails[]>([]);
  const [allEnterprisesInfo, setAllEnterprisesInfo] = useState<Enterprise[]>([]);
  const [folderUploadInfo, setFolderUploadInfo] = useState<FolderUploadInfo>();
  const [selectedEnterprise, setSelectedEnterprise] = useState<EnterpriseType>();
  const [openFoldersList, setOpenFoldersList] = useState<OpenFoldersListType[]>([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [showEnterpriseAddendum, setShowEnterpriseAddendum] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [accessTokenForModal, setAccessTokenForModal] = useState('');
  const addendumChecked = useRef(false);
  const sessionStartRef = useRef<number>(0);
  const cachedTokenRef = useRef<string>('');
  const hasTrackedLoginRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getValidAccessToken } = useGetValidAccessToken();
  const { allUsers, featuredUsers } = useGetAllUsers();
  const [profileUpdated] = useState(false);

  useEffect(() => {
    hotjar.initialize({
      id: parseInt(process.env.REACT_APP_HOTJAR_SITE_ID!, 10),
      sv: parseInt(process.env.REACT_APP_HOTJAR_SITE_VERSION!, 10),
    });
  }, []);

  const hideNavbar = routesWithoutNavbar.some((route) => location.pathname.includes(route));

  const hideFooter = routesWithoutFooter.some((route) => location.pathname.includes(route));

  const isPublicPage =
    location.pathname === '/privacy-policy' ||
    location.pathname === '/creator-agreement' ||
    location.pathname === '/enterprise-addendum' ||
    location.pathname === '/terms-of-use' ||
    location.pathname === '/signup' ||
    location.pathname === '/guest' ||
    location.pathname.includes('/share/') ||
    location.pathname.includes('explore') ||
    location.pathname.includes('user-profile');

  const shouldShowDrawer = !hiddenDrawerRoutes.includes(location.pathname);

  const handleSuccess = () => {
    setSuccessReg(true);
  };

  useEffect(() => {
    // /login keeps its straight shot to Auth0. Someone typing or bookmarking
    // it is a returning user who wants the password field, and making them
    // pass through the Welcome page's chooser first would cost them a click to
    // arrive where they already asked to go.
    //
    // /signup used to do the same, and no longer does. Its route renders
    // WelcomePage (see the Routes block), but this effect fired first, so the
    // browser left for Auth0 before the page could paint and the route was
    // dead code. Someone arriving from a marketing link landed in a bare
    // email-and-password form having seen nothing about the product.
    //
    // Now it renders the Welcome page and the visitor chooses -- guest, or
    // sign up -- and WelcomePage's own handlers call loginWithRedirect with
    // the same screen_hint this used to pass. The handoff to Auth0 is
    // unchanged and still happens for anyone creating an account; it just
    // happens after they have seen what they are signing up for.
    if (location.pathname === '/login') {
      loginWithRedirect({
        appState: { returnTo: '/my-tracks' },
      });
    }
  }, [location.pathname, loginWithRedirect]);

  // This is because we need to add every event on the page to mixpanel
  useEffect(() => {
    Mixpanel.track(window.location.pathname);
  }, [navigate]);

  useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        const idToken = await getIdTokenClaims();
        if (idToken && idToken.sub !== userId) {
          setUserId(idToken.sub);
        }
      } else if (isGuest) {
        // Guest sessions are not Auth0-authenticated, so getIdTokenClaims()
        // never resolves. Use the guest session id as the userId so the same
        // userId-keyed services (GetUser, S3 creds, storage) work without a
        // parallel codepath. Read through the backend-scoped store so the id
        // always matches the token the current backend will accept.
        const stored = getStoredGuestSession()?.sessionId ?? null;
        if (stored && stored !== userId) {
          setUserId(stored);
        }
      }
    })();
  }, [getIdTokenClaims, isAuthenticated, isGuest, userId]);

  const getStorageUsed = useCallback(async () => {
    const accessToken = await getValidAccessToken();
    const storageUse = await getStorageUsage(userId, accessToken, selectedEnterprise?.id);
    typeof storageUse === 'number' ? setStorageUsage(storageUse) : setStorageUsage(0);
  }, [getValidAccessToken, selectedEnterprise?.id, userId]);

  const getMostRecentTrackTimestamp = useCallback(async (): Promise<string | null> => {
    try {
      const accessToken = await getValidAccessToken();
      const trackFilterParams: TrackFilterParams = {
        userIds: [userId],
        sortBy: '', // Sort by creation date
        isAscending: false, // Most recent first
        genreList: [],
        artistList: [],
        collaboratorsList: [],
        TrackPrivacy: '',
        page: 0,
        limit: 1, // Only get the most recent track
        bpmStartValue: 0,
        bpmEndValue: 500,
        status: '',
        isCompletedTracks: false,
      };

      const response = await fetchTracks(
        accessToken,
        trackFilterParams,
        '',
        undefined,
        undefined,
        undefined,
        undefined
      );

      if (response instanceof GetMyTracksResponse) {
        const tracksList = response.getTracksList();
        if (tracksList.length > 0) {
          const mostRecentTrackResponse = tracksList[0];
          const track = mostRecentTrackResponse.getTrack();
          if (track) {
            return track.getTimestamp().toString();
          }
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }, [getValidAccessToken, userId]);

  const { getUserResponse, getUserError, isUserLoading, refreshUserDetails } = useGetUser(
    userId,
    profileUpdated
  );
  const { s3Credentials: credentials } = useS3Credentials(userId);

  useEffect(() => {
    const handleGetAllEnterprisesInfo = async () => {
      if (getUserResponse && isAuthenticated) {
        const enterpriseIds = getUserResponse
          .getEnterpriseDetailsList()
          .map((enterpriseDetail) => enterpriseDetail.getEnterpriseId());
        const t = await getValidAccessToken();
        const allEnterprisesInfo = await getAllUserEnterprises(t, enterpriseIds);
        if (allEnterprisesInfo instanceof GetEnterprisesForUserResponse) {
          setAllEnterprisesInfo(allEnterprisesInfo.getEnterpriseList());
        }
      }
    };
    handleGetAllEnterprisesInfo();
  }, [getUserResponse, getValidAccessToken, isAuthenticated]);

  useEffect(() => {
    // Fire as soon as Auth0 resolves — no artificial delay. Guests are
    // intentionally unauthenticated (no Auth0 session) so must not be bounced.
    if (isLoading) return;
    if (!isAuthenticated && !isPublicPage && !isGuest) {
      navigate('/welcome');
    }
  }, [isLoading, isAuthenticated, isPublicPage, isGuest, navigate]);

  useEffect(() => {
    // Guests skip the username-completion redirect — they have a synthetic
    // "guest-xxxxxxxx" username assigned server-side and there's nothing to
    // complete until they convert to a real account.
    if (isGuest) return;
    if (
      getUserResponse?.getUsername() === '' ||
      getUserResponse?.getUsername().includes('dummy_ommuse')
    ) {
      navigate('initial-setting');
      return;
    }
  }, [getUserResponse, isGuest, navigate]);

  const INVALID_EMAIL_RE = /[/]/;
  const BAD_EMAIL_LIST = ['y/n23@gmail.com', '201.00312.13.0127@education.gov.bt'];

  const emailNeedsCorrection = (email: string): boolean => {
    if (BAD_EMAIL_LIST.includes(email)) return true;
    if (INVALID_EMAIL_RE.test(email)) return true;
    return false;
  };

  useEffect(() => {
    if (!getUserResponse) return;
    // Guests are intentionally profile-less — no email, no first/last name.
    // The whole point of guest mode is "try the studio without signing up";
    // popping CompleteNameModal / CorrectEmailModal here would defeat that.
    // These modals fire after the user converts to a real account.
    if (isGuest) return;
    const firstName = getUserResponse.getFirstName();
    const lastName = getUserResponse.getLastName();
    const email = getUserResponse.getEmail ? getUserResponse.getEmail() : (user?.email ?? '');

    if (emailNeedsCorrection(email)) {
      setShowEmailModal(true);
      Mixpanel.track('profile_modal_shown', { modal: 'correct_email', userId });
    } else if (!firstName || !lastName) {
      setShowNameModal(true);
      Mixpanel.track('profile_modal_shown', { modal: 'complete_name', userId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserResponse, isGuest]);

  const handleNameSave = async (firstName: string, lastName: string) => {
    if (!getValidAccessToken || !userId || !getUserResponse) return;
    const token = await getValidAccessToken();
    const profile: Profile = {
      firstName,
      lastName,
      username: getUserResponse.getUsername(),
      email: getUserResponse.getEmail ? getUserResponse.getEmail() : (user?.email ?? ''),
      bio: getUserResponse.getBio(),
      connectedWallet: getUserResponse.getWalletAddressesList()[0] ?? '',
      skills: getUserResponse.getSkillsList(),
      genres: getUserResponse.getGenresList(),
      profilePic: getUserResponse.getProfilePicture(),
    };
    await registerUser(
      profile,
      null,
      userId,
      user,
      null,
      token,
      undefined,
      undefined,
      undefined,
      getUserResponse.getEnterpriseDetailsList()
    );
    await refreshUserDetails();
    setShowNameModal(false);
    Mixpanel.track('profile_modal_completed', { modal: 'complete_name', userId });
  };

  const handleEmailSave = async (newEmail: string) => {
    if (!getValidAccessToken || !userId) return;
    const token = await getValidAccessToken();
    await updateEmail(userId, newEmail, token);
    await refreshUserDetails();
    setShowEmailModal(false);
    Mixpanel.track('profile_modal_completed', { modal: 'correct_email', userId });
  };

  useEffect(() => {
    const runEffect = async () => {
      if (!isLoading) {
        if (isAuthenticated && getUserError && !isPublicPage && !successReg) {
          navigate('/initial-setting');
        } else if (getUserResponse) {
          try {
            const mostRecentTrackTimestamp = await getMostRecentTrackTimestamp();
            if (getUserResponse.getUuid().length > 0 && storageUsage !== null) {
              const uuid = getUserResponse.getUuid();
              const aliasKey = `mp_alias_done:${uuid}`;
              if (!localStorage.getItem(aliasKey)) {
                Mixpanel.alias(uuid); // merge current anon -> uuid
                localStorage.setItem(aliasKey, '1');
              }
              // Always identify on authenticated sessions
              Mixpanel.identify(uuid);
              Mixpanel.people.set({
                first_name: getUserResponse.getFirstName(),
                last_name: getUserResponse.getLastName(),
                username: getUserResponse.getUsername(),
                $email: getUserResponse.getEmail(),
                subscription_tier: tierMap[getUserResponse.getSubscriptionTier()],
                subscription_state: stateMap[getUserResponse.getSubscriptionState()],
              });
              const fixedStorage = Number(storageUsage.toFixed(3));
              if (selectedEnterprise?.name ? selectedEnterprise.name !== 'ommuse' : false) {
                const enterpriseName = selectedEnterprise?.name?.toLowerCase().replace(/\s+/g, '_');
                Mixpanel.people.set({
                  [`storage_usage_${enterpriseName}`]: fixedStorage,
                });
              } else {
                Mixpanel.people.set({ storage_usage: fixedStorage });
              }

              if (mostRecentTrackTimestamp) {
                Mixpanel.people.set({
                  most_recent_track_timestamp: mostRecentTrackTimestamp,
                });
              }
            }

            if (!isGuest) getStorageUsed();

            const stripeCustomerId = getUserResponse.getStripeCustomerId();
            const currentPath = window.location.pathname;
            const enterpriseName = selectedEnterprise?.name;
            const isInitialPath = ['/', '/initial-setting', '/signup', '/welcome'].includes(
              currentPath
            );

            if (!stripeCustomerId) {
              if (enterpriseName === 'ommuse') {
                navigate('/pricing-table');
              } else if (isEnterprise && isInitialPath) {
                navigate('/my-tracks');
              }
            } else if (isInitialPath) {
              navigate('/my-tracks');
            }
          } catch (error) {}
        }
      }
    };

    runEffect();
  }, [
    getStorageUsed,
    getMostRecentTrackTimestamp,
    storageUsage,
    getUserError,
    getUserResponse,
    isAuthenticated,
    isGuest,
    isLoading,
    isPublicPage,
    navigate,
    selectedEnterprise,
    successReg,
  ]);

  const authContextValue = useMemo(() => ({ userId }), [userId]);
  const userDetailsContextValue = useMemo(
    () => ({
      userName: getUserResponse?.getUsername() ?? '',
      fullName: getUserResponse?.getFirstName() + ' ' + getUserResponse?.getLastName(),
      connectedWallet: getUserResponse?.getWalletAddressesList()[0] ?? '',
      storageUsage: storageUsage ?? 0,
      stripeCustomerId: getUserResponse?.getStripeCustomerId() ?? '',
      subscriptionState:
        getUserResponse?.getSubscriptionState() ?? SubscriptionState.SUBSCRIPTION_STATE_UNSPECIFIED,
      subscriptionTier:
        getUserResponse?.getSubscriptionTier() ?? SubscriptionTier.SUBSCRIPTION_TIER_UNSPECIFIED,
      allUsers: allUsers,
      featuredUsers: featuredUsers,
      hasPaymentMethod: hasPaymentMethod,
      enterprise: selectedEnterprise,
      role: selectedEnterprise?.role ?? '',
      isEnterpriseAccount: selectedEnterprise?.name ? selectedEnterprise.name !== 'ommuse' : false,
      enterprises: getUserResponse?.getEnterpriseDetailsList() ?? [],
    }),
    [getUserResponse, storageUsage, allUsers, featuredUsers, hasPaymentMethod, selectedEnterprise]
  );

  const s3CredentialsContextValue = useMemo(
    () => ({
      awsAccessKeyId: credentials?.awsAccessKeyId,
      awsSecretAccessKey: credentials?.awsSecretAccessKey,
      awsSessionToken: credentials?.awsSessionToken,
      expiry: credentials?.expiry,
    }),
    [credentials]
  );

  useEffect(() => {
    if (isAuthenticated && userId) {
      const handleUseEffect = async () => {
        if (!userId) return;
        const token = await getValidAccessToken();
        const getMethod = await getPaymentMethod(token, userId);
        if (getMethod) setHasPaymentMethod(getMethod.getHasPaymentMethod());
      };
      handleUseEffect();
    }
  }, [getValidAccessToken, isAuthenticated, userId]);

  useEffect(() => {
    const storedEnterpriseJSON = sessionStorage.getItem('enterprise');
    if (getUserResponse?.getUserId()) {
      setEnterprises(getUserResponse.getEnterpriseDetailsList());
      if (getUserResponse.getEnterpriseDetailsList().length === 1) {
        const e = getUserResponse.getEnterpriseDetailsList()[0];
        const newEnterprise: EnterpriseType = {
          id: e.getEnterpriseId(),
          name: e.getEnterpriseName(),
          image: allEnterprisesInfo.length > 0 ? (allEnterprisesInfo[0].getImage() ?? '') : '',
          role: e.getRole(),
          storageLimit:
            allEnterprisesInfo.length > 0 ? (allEnterprisesInfo[0].getStorageLimit() ?? 0) : 0,
        };
        sessionStorage.setItem('enterprise', JSON.stringify(newEnterprise));
      }
    }
    if (!storedEnterpriseJSON) {
      if (
        window.location.pathname !== '/initial-setting' &&
        window.location.pathname !== '/pricing-table' &&
        !isLoading &&
        isAuthenticated &&
        enterprises.length > 0 &&
        !isPublicPage
      ) {
        navigate('/select-enterprise');
        return;
      }
    } else {
      const e: EnterpriseType = JSON.parse(storedEnterpriseJSON);
      const matchingEnterprise: EnterpriseDetails | undefined = enterprises.find(
        (ent) => ent.getEnterpriseId() === e.id
      );
      const matchingEnterpriseForImage = allEnterprisesInfo.find((ent) => ent.getId() === e.id);
      if (matchingEnterprise) {
        const UpdatedEnterprise: EnterpriseType = {
          id: e.id,
          name: e.name,
          image:
            allEnterprisesInfo.length > 0 ? (matchingEnterpriseForImage?.getImage() ?? '') : '',
          role: matchingEnterprise.getRole(),
          storageLimit:
            allEnterprisesInfo.length > 0
              ? (matchingEnterpriseForImage?.getStorageLimit() ?? 0)
              : 0,
        };
        sessionStorage.setItem('enterprise', JSON.stringify(UpdatedEnterprise));
        setSelectedEnterprise(UpdatedEnterprise);
      } else setSelectedEnterprise(e);
    }
  }, [
    allEnterprisesInfo,
    enterprises,
    enterprises.length,
    getUserResponse,
    getValidAccessToken,
    isAuthenticated,
    isLoading,
    isPublicPage,
    navigate,
    userId,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !selectedEnterprise || !userId) return;

    sessionStartRef.current = Date.now();

    const sendActivity = async (sessionDurationSeconds: number) => {
      const token = await getValidAccessToken();
      cachedTokenRef.current = token;
      handleLastActive(userId, token, selectedEnterprise.id, sessionDurationSeconds);
    };

    // Only fire the login ping once per authenticated session, not on every effect re-run
    if (!hasTrackedLoginRef.current) {
      hasTrackedLoginRef.current = true;
      sendActivity(0);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        // Use cached token — synchronous path, no await
        handleLastActive(userId, cachedTokenRef.current, selectedEnterprise.id, elapsed);
      } else {
        sessionStartRef.current = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      // Use cached token — beforeunload cannot await async calls
      handleLastActive(userId, cachedTokenRef.current, selectedEnterprise.id, elapsed);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [getValidAccessToken, isAuthenticated, selectedEnterprise, userId]);

  useEffect(() => {
    if (getUserResponse?.getStripeCustomerId() === '' && selectedEnterprise?.name === 'ommuse') {
      navigate('/pricing-table');
      return;
    }
  }, [enterprises.length, getUserResponse, selectedEnterprise, navigate]);

  useEffect(() => {
    Mixpanel.track('App Open', {
      timestamp: new Date().toISOString(),
      userName: getUserResponse?.getUsername() || '',
    });
  }, [getUserResponse]);

  // Unix timestamp for April 3, 2025 00:00:00 UTC — addendum applies to members added from this date onward
  const ENTERPRISE_ADDENDUM_CUTOFF = 1743638400;

  useEffect(() => {
    if (
      addendumChecked.current ||
      !isAuthenticated ||
      !userId ||
      !selectedEnterprise ||
      selectedEnterprise.name === 'ommuse'
    )
      return;

    const enterpriseDetail = getUserResponse
      ?.getEnterpriseDetailsList()
      .find((e) => e.getEnterpriseId() === selectedEnterprise.id);
    const role = Number(enterpriseDetail?.getRole() ?? selectedEnterprise.role);
    const dateAdded = enterpriseDetail?.getDateAdded() ?? 0;
    if (role !== Role.ROLE_ADMIN || dateAdded < ENTERPRISE_ADDENDUM_CUTOFF) {
      return;
    }

    addendumChecked.current = true;
    (async () => {
      const token = await getValidAccessToken();
      const accepted = await checkEnterpriseAddendumAccepted(token);
      if (!accepted) {
        setAccessTokenForModal(token);
        setShowEnterpriseAddendum(true);
      }
    })();
  }, [isAuthenticated, userId, selectedEnterprise, getUserResponse, getValidAccessToken]);

  if (
    !isPublicPage &&
    (isLoading ||
      (isAuthenticated &&
        !selectedEnterprise &&
        location.pathname !== '/select-enterprise' &&
        window.location.pathname !== '/initial-setting'))
  ) {
    return <Loading />;
  }

  return (
    <>
      {/* These modals are lazy chunks outside the route boundary — without this a
          chunk that cannot load unmounts the whole app to a blank page. */}
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <ConsentModal
            open={showEnterpriseAddendum}
            accessToken={accessTokenForModal}
            onAccept={() => setShowEnterpriseAddendum(false)}
          />
          <CompleteNameModal
            open={showNameModal && !isGuest}
            initialFirstName={user?.given_name ?? ''}
            initialLastName={user?.family_name ?? ''}
            onComplete={handleNameSave}
          />
          <CorrectEmailModal
            open={showEmailModal && !isGuest}
            currentEmail={user?.email ?? ''}
            onComplete={handleEmailSave}
          />
        </Suspense>
      </ErrorBoundary>
      <AuthContext.Provider value={authContextValue}>
        <UserDetailsContext.Provider value={userDetailsContextValue}>
          <S3CredentialsContext.Provider value={s3CredentialsContextValue}>
            <div
              style={{
                position: 'relative',
                minHeight: !hideFooter ? '100vh' : 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <GuestClaimGate />
              <GuestTermsGateProvider />
              <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                {shouldShowDrawer && (
                  <DrawerContainer>
                    <Drawer currentUser={getUserResponse} />
                  </DrawerContainer>
                )}
                {/* minWidth 0: without it this flex item's min-width:auto adopts the
                    widest page content's intrinsic width, and mobile browsers stretch
                    the layout viewport to match (pages render zoomed-out/clipped). */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {isUploading && (
                    <FileUploadStatusCard
                      uploadingFilesDetailsList={uploadingFilesDetailsList}
                      setIsUploading={setIsUploading}
                      uploadingCardsStatusList={uploadingCardsStatusList}
                      setUploadingCardsStatusList={setUploadingCardsStatusList}
                      isProcessing={isProcessing}
                      folderUploadInfo={folderUploadInfo}
                    />
                  )}
                  {!hideNavbar && <ResponsiveNavbar currentUser={getUserResponse} />}
                  <PageLayout hideNavbar={hideNavbar}>
                    <ErrorBoundary>
                      <Suspense fallback={<Loading />}>
                        <Routes>
                          <Route path="/share-management" element={<ShareManagement />} />
                          <Route path="/not-found" element={<NotFound />} />
                          <Route
                            path="/team-management"
                            element={<TeamManagement currentUser={getUserResponse} />}
                          />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/creator-agreement" element={<CreatorAgreement />} />
                          <Route path="/enterprise-addendum" element={<EnterpriseAddendum />} />
                          <Route path="/terms-of-use" element={<TermsOfUse />} />
                          <Route path="/explore" element={<ExplorePage />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route
                            path="/my-tracks"
                            element={
                              <TracksPage
                                setUploadingFilesDetailsList={setUploadingFilesDetailsList}
                                setIsUploading={setIsUploading}
                                setUploadingCardsStatusList={setUploadingCardsStatusList}
                                setIsUploadProcessing={setIsProcessing}
                                setFolderUploadInfo={setFolderUploadInfo}
                                setOpenFoldersList={setOpenFoldersList}
                                getStorageUsed={getStorageUsed}
                              />
                            }
                          />
                          <Route
                            path={`/share/:id`}
                            element={
                              <SharedContentPage
                                currentUser={getUserResponse}
                                isUserLoading={isUserLoading}
                              />
                            }
                          />
                          <Route
                            path={`/share/:name/:id`}
                            element={<SharedFolderPage isUserLoading={isUserLoading} />}
                          />
                          <Route
                            path={`/share/project/:name/:id`}
                            element={<SharedFolderPage isUserLoading={isUserLoading} />}
                          />
                          <Route
                            path={`/share/folder/:name/:id`}
                            element={<SharedFolderPage isUserLoading={isUserLoading} />}
                          />
                          <Route
                            path={`/share/projects/:id`}
                            element={<SharedProjectsPage isUserLoading={isUserLoading} />}
                          />
                          <Route
                            path={`/share/projects/:id/project/:name/:projectId`}
                            element={<SharedFolderPage isUserLoading={isUserLoading} />}
                          />
                          <Route
                            path="/initial-setting"
                            element={<InitialSettings handleSuccess={handleSuccess} />}
                          />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route
                            path="/settings"
                            element={<Navigate to="/profile" replace />}
                          />
                          <Route
                            path="/profile-settings"
                            element={<Navigate to="/profile" replace />}
                          />
                          <Route path="/user-profile/:usr" element={<UserProfile />} />
                          <Route path="/guest" element={<GuestAutoStartPage />} />
                          <Route path="/welcome" element={<WelcomePage />} />
                          <Route path="/logout" element={<WelcomePage />} />
                          {process.env.REACT_APP_ENV !== 'production' && (
                            <Route path="/dev/components" element={<ComponentsPreview />} />
                          )}
                          {!isEnterprise && (
                            <Route path="/pricing-table" element={<PricingTable />} />
                          )}
                          <Route
                            path="*"
                            element={
                              isAuthenticated ? (
                                <Navigate to="/not-found" replace={true} />
                              ) : (
                                <Navigate to="/welcome" replace={true} />
                              )
                            }
                          />
                          <Route
                            path="/select-enterprise"
                            element={
                              <SelectEnterprise
                                enterprises={enterprises}
                                allEnterprisesInfo={allEnterprisesInfo}
                              />
                            }
                          />
                          <Route
                            path="/projects"
                            element={
                              <Projects
                                openFoldersList={openFoldersList}
                                setOpenFoldersList={setOpenFoldersList}
                              />
                            }
                          />
                          <Route
                            path="/project/:name/:id"
                            element={
                              <TracksPage
                                setUploadingFilesDetailsList={setUploadingFilesDetailsList}
                                setIsUploading={setIsUploading}
                                setUploadingCardsStatusList={setUploadingCardsStatusList}
                                setIsUploadProcessing={setIsProcessing}
                                openFoldersList={openFoldersList}
                                setOpenFoldersList={setOpenFoldersList}
                                setFolderUploadInfo={setFolderUploadInfo}
                                getStorageUsed={getStorageUsed}
                              />
                            }
                          />
                          <Route
                            path="/project/:name/:id/folder/:folderId"
                            element={
                              <TracksPage
                                setUploadingFilesDetailsList={setUploadingFilesDetailsList}
                                setIsUploading={setIsUploading}
                                setUploadingCardsStatusList={setUploadingCardsStatusList}
                                setIsUploadProcessing={setIsProcessing}
                                openFoldersList={openFoldersList}
                                setOpenFoldersList={setOpenFoldersList}
                                setFolderUploadInfo={setFolderUploadInfo}
                                getStorageUsed={getStorageUsed}
                              />
                            }
                          />
                          <Route path="/" element={<DefaultPage />} />
                          {!isEnterprise && <Route path="/billing" element={<Billing />} />}
                          {isEnterprise &&
                            selectedEnterprise?.role !== undefined &&
                            Number(selectedEnterprise.role) === Role.ROLE_ADMIN && (
                              <Route path="/billing" element={<Billing />} />
                            )}
                          {user?.email?.endsWith('@ommuse.com') && (
                            <>
                              <Route
                                path="/admin/support-tickets"
                                element={<SupportTicketsPage />}
                              />
                              <Route path="/admin/platform-stats" element={<AdminStatsPage />} />
                            </>
                          )}
                          <Route path="/success" element={<Success />} />
                          <Route path="/payment-cancel" element={<PaymentCancel />} />
                          <Route path="/signup" element={<WelcomePage />} />
                          <Route path="/login"></Route>
                        </Routes>
                      </Suspense>
                    </ErrorBoundary>
                  </PageLayout>
                </div>
              </div>
              {!hideFooter && <Footer />}
            </div>
          </S3CredentialsContext.Provider>
        </UserDetailsContext.Provider>
      </AuthContext.Provider>
    </>
  );
};

export default App;
